import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Car, MonitorSmartphone, Package } from 'lucide-react';
import { useItems } from '@/hooks/useItems';
import { useFamily } from '@/context/FamilyContext';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { EmptyState } from '@/components/EmptyState';
import { TopBar } from '@/components/TopBar';
import type { Item, ItemType } from '@/types';

const tabs: { label: string; type: ItemType | 'all' }[] = [
  { label: 'All', type: 'all' },
  { label: 'Vehicles', type: 'vehicle' },
  { label: 'Appliances', type: 'appliance' }
];

export function Items() {
  const navigate = useNavigate();
  const { canEdit } = useFamily();
  const [tab, setTab] = useState<ItemType | 'all'>('all');
  const [search, setSearch] = useState('');
  const { items, loading } = useItems(tab === 'all' ? undefined : tab);

  const filtered = items.filter((item) => {
    const q = search.toLowerCase();
    return (
      item.name.toLowerCase().includes(q) ||
      (item.brand ?? '').toLowerCase().includes(q) ||
      (item.model ?? '').toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <TopBar
        title="Items"
        right={
          canEdit && (
            <button
              onClick={() => navigate('/items/new')}
              className="flex items-center gap-1 px-3 py-1.5 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          )
        }
      />

      <div className="px-4 pt-3 pb-4 space-y-3">
        {/* Tabs */}
        <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
          {tabs.map((t) => (
            <button
              key={t.type}
              onClick={() => setTab(t.type)}
              className={`flex-1 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                tab === t.type ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, brand, model…"
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
          />
        </div>

        {loading && <LoadingSpinner className="py-12" />}

        {!loading && filtered.length === 0 && (
          <EmptyState
            icon={<Package className="w-7 h-7" />}
            title="No items found"
            description={search ? 'Try a different search.' : 'Tap the Add button to add your first vehicle or appliance.'}
            action={
              canEdit && !search && (
                <button
                  onClick={() => navigate('/items/new')}
                  className="mt-2 px-4 py-2.5 bg-brand-600 text-white text-sm font-medium rounded-xl hover:bg-brand-700 transition-colors"
                >
                  Add first item
                </button>
              )
            }
          />
        )}

        {!loading && filtered.length > 0 && (
          <div className="space-y-3">
            {filtered.map((item) => <ItemCard key={item.id} item={item} onClick={() => navigate(`/items/${item.id}`)} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function ItemCard({ item, onClick }: { item: Item; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white border border-gray-100 rounded-xl p-4 shadow-sm hover:border-brand-200 hover:shadow-md active:scale-[0.99] transition-all"
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
          item.type === 'vehicle' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'
        }`}>
          {item.type === 'vehicle' ? <Car className="w-5 h-5" /> : <MonitorSmartphone className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 text-sm">{item.name}</p>
          <p className="text-xs text-gray-500 truncate">
            {[item.brand, item.model].filter(Boolean).join(' · ') || (item.type === 'vehicle' ? 'Vehicle' : 'Appliance')}
          </p>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          item.type === 'vehicle' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'
        }`}>
          {item.type === 'vehicle' ? 'Vehicle' : 'Appliance'}
        </span>
      </div>
    </button>
  );
}
